import { z } from "zod";
import { keepPreviousData } from "@tanstack/react-query";

import { apiFetch } from "@/lib/jwt-auth-client";

import { VideoProjectSchema, type VideoEditSpec, type VideoProject } from "../types";
import { patchProject } from "./videoProjectApi";
import {
  mapEditorSnapshotToVideoEditSpecDraft,
  type UnsupportedVideoAdSnapshotField,
} from "../lib/videoAdSnapshotMapper";

export const VIDEO_AD_EDITED_OUTPUT_SUBJECT = "video-ads:edited-output";

const VideoAdSourceArtifactSchema = z.object({
  id: z.string(),
  name: z.string(),
  subject: z.string().nullable(),
  workspaceId: z.string().nullable(),
  videoUrl: z.string(),
  width: z.number().nullable().optional(),
  height: z.number().nullable().optional(),
  durationSec: z.number().nullable().optional(),
  draftEditorSnapshot: z.unknown().nullable().optional(),
});

const ResolveVideoAdArtifactResponseSchema = z.object({
  project: VideoProjectSchema,
  sourceArtifact: VideoAdSourceArtifactSchema,
  reused: z.boolean(),
  snapshotMigrationRequired: z.boolean(),
});

const SaveArtifactResponseSchema = z.object({
  id: z.string(),
  created: z.boolean().optional(),
});

export type VideoAdSourceArtifact = z.infer<typeof VideoAdSourceArtifactSchema>;

export interface ResolveVideoAdArtifactResult {
  project: VideoProject;
  sourceArtifact: VideoAdSourceArtifact;
  reused: boolean;
  migratedLegacySnapshot: boolean;
  migrationUnsupported: UnsupportedVideoAdSnapshotField[];
}

async function jsonOrThrow<T>(res: Response, schema: z.ZodType<T>): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg = (body as { error?: string }).error ?? `Request failed (${res.status})`;
    throw new Error(msg);
  }
  const data: unknown = await res.json();
  return schema.parse(data);
}

/** React Query key for a resolved video-ad editor project. */
export function videoAdEditorProjectKey(artifactId: string | null | undefined) {
  return ["video-ad-editor-project", artifactId ?? ""] as const;
}

/**
 * Shared `useQuery`/`prefetchQuery` options for resolving a video-ad artifact
 * into its editor project. Cached aggressively so re-opening the same editor
 * (route or modal) paints from cache instead of re-showing the full-screen
 * spinner and re-issuing the resolve POST. `placeholderData: keepPreviousData`
 * keeps the last project on screen through a background revalidation.
 */
export function resolveVideoAdArtifactQueryOptions(
  artifactId: string | null | undefined,
) {
  return {
    queryKey: videoAdEditorProjectKey(artifactId),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      resolveVideoAdArtifactProject(artifactId!, signal),
    enabled: Boolean(artifactId),
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    placeholderData: keepPreviousData,
  };
}

export async function resolveVideoAdArtifactProject(
  artifactId: string,
  signal?: AbortSignal,
): Promise<ResolveVideoAdArtifactResult> {
  const res = await apiFetch("/api/videos/resolve-artifact/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ artifactId }),
    signal,
  });
  const resolved = await jsonOrThrow(res, ResolveVideoAdArtifactResponseSchema);
  let project = resolved.project;
  let migratedLegacySnapshot = false;
  let migrationUnsupported: UnsupportedVideoAdSnapshotField[] = [];

  if (resolved.snapshotMigrationRequired && resolved.sourceArtifact.draftEditorSnapshot) {
    const mapped = mapEditorSnapshotToVideoEditSpecDraft({
      snapshot: resolved.sourceArtifact.draftEditorSnapshot,
      baseVideoUrl: resolved.sourceArtifact.videoUrl,
      sourceStorageKey: project.videoStorageKey,
      sourceMimeType: project.mimeType,
      baseDurationSec: resolved.sourceArtifact.durationSec ?? null,
      language: project.transcript?.language ?? null,
    });
    migrationUnsupported = mapped.unsupported;
    project = await patchProject(
      project.id,
      {
        ...mapped.patch,
        legacyEditorSnapshotMigratedAt: new Date().toISOString(),
        legacyEditorSnapshotUnsupported: mapped.unsupported,
        legacyEditorSnapshotVersion:
          typeof (resolved.sourceArtifact.draftEditorSnapshot as { version?: unknown }).version ===
          "number"
            ? (resolved.sourceArtifact.draftEditorSnapshot as { version: number }).version
            : null,
      },
      signal,
    );
    migratedLegacySnapshot = true;
  }

  return {
    project,
    sourceArtifact: resolved.sourceArtifact,
    reused: resolved.reused,
    migratedLegacySnapshot,
    migrationUnsupported,
  };
}

export interface SaveEditedVideoAdArtifactInput {
  project: VideoProject;
  sourceArtifact: VideoAdSourceArtifact;
  editedVideoUrl: string;
  exportKey?: string;
  editorSpec?: VideoEditSpec | null;
  width?: number | null;
  height?: number | null;
}

export async function saveEditedVideoAdArtifact(
  input: SaveEditedVideoAdArtifactInput,
  signal?: AbortSignal,
): Promise<{ id: string; created?: boolean }> {
  const width =
    input.width ?? input.editorSpec?.dimensions?.width ?? input.sourceArtifact.width ?? null;
  const height =
    input.height ?? input.editorSpec?.dimensions?.height ?? input.sourceArtifact.height ?? null;
  const res = await apiFetch("/api/artifacts/save/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name: `${input.sourceArtifact.name} - edit`,
      subject: VIDEO_AD_EDITED_OUTPUT_SUBJECT,
      type: "video",
      content_md: "",
      metadata: {
        videoUrl: input.editedVideoUrl,
        outputSubject: VIDEO_AD_EDITED_OUTPUT_SUBJECT,
        editedFromArtifactId: input.sourceArtifact.id,
        sourceArtifactId: input.sourceArtifact.id,
        sourceArtifactSubject: input.sourceArtifact.subject,
        editorProjectId: input.project.id,
        renderExportKey: input.exportKey,
        sourceVideoUrl: input.project.videoUrl,
        editorSpec: input.editorSpec ?? null,
        width,
        height,
      },
      workspace_id: input.project.workspaceId ?? input.sourceArtifact.workspaceId,
      coagent_type: "ads",
    }),
    signal,
  });
  return jsonOrThrow(res, SaveArtifactResponseSchema);
}
